import React, { useEffect, useRef, useState } from "react";
import BreadcrumbOrder from "../Components/Layout/BreadcrumbOrder";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Cart from "../Components/Layout/Cart/Cart";
import { ORDER_TYPE_DELIVERY_ID, ORDER_TYPE_DINE_IN_ID, ORDER_TYPE_ICONS_BY_ID, ORDER_TYPE_STORE_COUNTER_ID, ORDER_TYPE_TAKEAWAY_ID } from "../Utils/Constants/Service";
import { TbBuildingSkyscraper, TbCashRegister, TbChevronDown, TbClock, TbMessage2Question } from "react-icons/tb";
import { goScrollTo, sanitizeText, vibrate } from "../Utils/Helpers";
import { useStoreContext } from "../Contexts/StoreContext";
import DefaultLayout from "../Layouts/DefaultLayout";
import { useOrderTypeContext } from "../Contexts/OrderTypeContext";
import OrderService from "../Services/OrderService";
import { useLoginContext } from "../Contexts/LoginContext";
import { METHOD_PAYMENT_TYPE_PIX, ORDER_STATUS_AWAITING_PAYMENT, ORDER_STATUS_CANCELED, ORDER_STATUS_DONE, ORDER_STATUS_EXPEDITED, ORDER_STATUS_EXPIRED, ORDER_STATUS_MOUNTING, ORDER_STATUS_OPEN, ORDER_STATUS_PENDING, ORDER_STATUS_RELEASED_SHIPMENT, ORDER_STATUS_RELEASED_TAKEAWAY } from "../Utils/Constants/Checkout";
import { useCheckoutContext } from "../Contexts/CheckoutContext";
import { useLayoutContext } from "../Contexts/LayoutContext";
import { useCartHook } from "../Hooks/CartHook";
import { useCartContext } from "../Contexts/CartContext";
import { ImSpinner8 } from "react-icons/im";
import { getOrderTypeLabelById, ORDER_CART_NOT_FOUND } from "../Utils/Constants/Order";
import { useStoreHook } from "../Hooks/StoreHook";
import QRCodePix from "../Components/Layout/Checkout/QRCodePix";
import StampStatus from "../Components/Layout/StampStatus";
import { formatCartId } from "../Utils/Formatters";
import BoxContentQRCodeOrder from "../Components/Layout/Cart/BoxContentQRCodeOrder";
import Button from "../Components/Form/Button";
import { useTranslation } from "react-i18next";
import { useOrderHook } from "../Hooks/OrderHook";
import Badge from "../Components/Layout/Badge";
import { MdOutlineTableRestaurant } from "react-icons/md";
import CardPointsEarned from "../Components/Layout/Cart/CardPointsEarned";

type OrderPlacedProps = {
    showHeader?: boolean;
    showFooter?: boolean;
    isInDrawer?: boolean;
}

const OrderPlaced = ({ showHeader, showFooter, isInDrawer }: OrderPlacedProps) => {

    const { t } = useTranslation();
    const { order_id } = useParams();
    const { currentStore } = useStoreContext();
    const { orderType, getOrderTypeIdByName} = useOrderTypeContext();
    const { isAuthenticated, user, refreshUser } = useLoginContext();
    const { order, setOrder, showPixCode, setShowPixCode } = useCheckoutContext();
    const { company, setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingIconSuccess, setMainLoadingTitle, setMainLoadingCloseButton, setMainLoadingMessage, setMainLoadingLabelCloseButton, isMobile, loadingCompany } = useLayoutContext();
    const { cartFinished, cancelPayment } = useCartHook();
    const { goToOrderView, getOrdersToday } = useOrderHook();
    const { getActiveCart, getLinkToHome, loadCompanyInfo } = useStoreHook();
    const { cartDisplay, setReturnToCheckout, currentOrder, currentOrderId } = useCartContext();

    const location = useLocation();
    const navigate = useNavigate();

    const [getOrder, setGetOrder] = useState<any | null>(currentOrder);
    const [localCart, setLocalCart] = useState<any | null>(order?.cart);
    const [loadingGetOrder, setLoadingGetOrder] = useState<boolean>(true);
    const [showBreadcrumbOrder, setShowBreadcrumbOrder] = useState<boolean>(false);
    const [showStampStatus, setShowStampStatus] = useState<boolean>(false);
    const [readNotifyReleased, setReadNotifyReleased] = useState<boolean>(false);
    const [showOrderQRCode, setShowOrderQRCode] = useState<boolean>(false);
    const [showOrderInfo, setShowOrderInfo] = useState<boolean>(false);
    const [messageByStatus, setMessageByStatus] = useState('');
    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [isReleased, setIsReleased] = useState<boolean>(false);
    const isFirstRender = useRef(true);
    const [ iconOrderType, setIconOrderType ] = useState<any>(null);

    const localOrderId = order_id || currentOrder?.id || currentOrderId;

    useEffect(() => {

        if (!isFirstRender.current && !isAuthenticated) {
            navigate(getLinkToHome());
            return;
        }

        setLoadingGetOrder(true);
        setReturnToCheckout(false);

        if (!isInDrawer) {
            document.title = t('order_placed.page_title', {orderId: formatCartId(Number(localOrderId))});
        }

        const handleOrderFinished = (order: any) => {
            setMainLoading(false);
            setShowPixCode(false);

            //* Atualiza os dados do usuário quando participa do programa de fidelidade para atualizar seus pontos
            if (user?.on_fidelity_program) {
                refreshUser();
            }

            if (order.type === ORDER_TYPE_STORE_COUNTER_ID) {
                cartFinished({
                    id: cartDisplay.id,
                    display_id: cartDisplay.display_id ? Number(cartDisplay.display_id) : null,
                    order: order
                });
                navigate(getLinkToHome());
            }
        }

        let intervalToCheckOrderStatus: NodeJS.Timeout | null = null;

        const getDetail = async () => {
            if (!localOrderId) {
                return;
            }

            const isOrderPlaced = location.pathname === `${getLinkToHome()}/order/${localOrderId}/placed`;
            const payload = { order_id: Number(localOrderId) };

            try {
                const orderService = new OrderService(orderType);
                const details = await orderService.detail(payload);
                setLoadingGetOrder(false);

                if (details) {
                    if (isFirstRender.current) {
                        isFirstRender.current = false;
                    } else if (isOrderPlaced) {
                        goToOrderView(Number(localOrderId));
                    }

                    setLocalCart(details.cart);
                    setShowBreadcrumbOrder([ORDER_STATUS_DONE, ORDER_STATUS_PENDING, ORDER_STATUS_MOUNTING, ORDER_STATUS_RELEASED_TAKEAWAY, ORDER_STATUS_EXPEDITED, ORDER_STATUS_OPEN, ORDER_STATUS_RELEASED_SHIPMENT].includes(details.status));
                    setShowStampStatus([ORDER_STATUS_CANCELED, ORDER_STATUS_EXPIRED].includes(details.status));
                    setGetOrder(details);
                    setOrder(details);
                    setIsValidated(Boolean(details.cart.cart_pdv_validation));

                    const includeIntervalToCheckOrder = Boolean(
                        details?.type === ORDER_TYPE_DELIVERY_ID
                            ?   details.status !== ORDER_STATUS_DONE && details.status !== ORDER_STATUS_EXPIRED && details.status !== ORDER_STATUS_CANCELED
                            :   !details.cart.cart_pdv_validation && details.status !== ORDER_STATUS_EXPIRED && details.status !== ORDER_STATUS_CANCELED
                    );

                    const isAnOrderCompletionStatus = [ORDER_STATUS_DONE, ORDER_STATUS_EXPIRED, ORDER_STATUS_CANCELED].includes(details.status);

                    if (includeIntervalToCheckOrder) {
                        if (!intervalToCheckOrderStatus) {
                            intervalToCheckOrderStatus = setInterval(() => {
                                getDetail();
                            }, 10000);
                        }
                    } else {
                        if (intervalToCheckOrderStatus) {
                            clearInterval(intervalToCheckOrderStatus);
                            intervalToCheckOrderStatus = null;
                        }
                        if (isAnOrderCompletionStatus) {
                            if (details.type === ORDER_TYPE_STORE_COUNTER_ID) {
                                if (details.status === ORDER_STATUS_DONE) {
                                    cartFinished({
                                        id: cartDisplay.id,
                                        display_id: cartDisplay.display_id ? Number(cartDisplay.display_id) : null,
                                        order: details
                                    });
                                    navigate(getLinkToHome());
                                }
                                //! O QUE FAZER QUANDO EXPIRAR OU CANCELAR NO AUTO?
                            }
                            if (order?.id === details.id) {
                                setOrder(null);
                            }
                        }
                    }

                    if (details.type !== ORDER_TYPE_STORE_COUNTER_ID && isFirstRender.current && order.status !== details.status && details.status !== ORDER_STATUS_AWAITING_PAYMENT) {
                        const orderTypeId = Number(getOrderTypeIdByName(orderType));
                        const payload = {
                            store_id: currentStore.id,
                            order_type_id: orderTypeId,
                            order_type: orderType,
                        }
                        getActiveCart(payload).catch(() => {});
                    }

                    setShowPixCode(details.payment_method.type === METHOD_PAYMENT_TYPE_PIX && details.status === ORDER_STATUS_AWAITING_PAYMENT);

                    if (details.status === ORDER_STATUS_DONE) {
                        handleOrderFinished(details);
                    }
                }
            } catch (error: any) {

                setLoadingGetOrder(false);

                if (error.response?.data?.error === ORDER_CART_NOT_FOUND) {
                    if (intervalToCheckOrderStatus) {
                        clearInterval(intervalToCheckOrderStatus);
                    }
                    setMainLoading(true);
                    setMainLoadingIcon(false);
                    setMainLoadingIconSuccess(false);
                    setMainLoadingIconError(true);
                    setMainLoadingTitle(t('order_placed.order_not_found_title'));
                    setMainLoadingMessage(t('order_placed.order_not_found_message', {orderId: formatCartId(Number(localOrderId))}));
                    setMainLoadingCloseButton(true);
                    setMainLoadingLabelCloseButton(t('layout.confirm.label_close_button'))
                } else {
                    //* Mensagem de erro genérica
                    setMainLoading(true);
                    setMainLoadingIcon(false);
                    setMainLoadingIconSuccess(false);
                    setMainLoadingIconError(true);
                    setMainLoadingTitle(t('order_placed.order_error_title'));
                    setMainLoadingMessage(t('order_placed.order_error_message', {orderId: formatCartId(Number(localOrderId))}));
                    setMainLoadingCloseButton(true);
                    setMainLoadingLabelCloseButton(t('layout.confirm.label_close_button'))
                }

                navigate(getLinkToHome());
            }
        }

        getDetail();

        return () => {
            if (intervalToCheckOrderStatus) {
                clearInterval(intervalToCheckOrderStatus);
            }
        };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localOrderId, currentOrderId, isFirstRender]);

    useEffect(() => {

        //* Mensagem "Released" somente para Dine-in ou Takeaway
        setIsReleased(
            Boolean(
                getOrder
                && (getOrder.type === ORDER_TYPE_DINE_IN_ID || getOrder.type === ORDER_TYPE_TAKEAWAY_ID)
                && (getOrder.status === ORDER_STATUS_RELEASED_TAKEAWAY || getOrder.status === ORDER_STATUS_RELEASED_SHIPMENT)
            )
        );

        setShowOrderQRCode(
            getOrder
            && getOrder.type !== ORDER_TYPE_STORE_COUNTER_ID
            && getOrder.type !== ORDER_TYPE_DELIVERY_ID
            && !showPixCode
            && getOrder
            && getOrder.status !== ORDER_STATUS_AWAITING_PAYMENT
            && getOrder.status !== ORDER_STATUS_OPEN
            && getOrder.status !== ORDER_STATUS_EXPIRED
            && getOrder.status !== ORDER_STATUS_CANCELED
        );

        if (!showOrderInfo) {
            setShowOrderInfo(
                getOrder
                && (
                    getOrder.type === ORDER_TYPE_DELIVERY_ID
                    || getOrder?.type === ORDER_TYPE_DELIVERY_ID
                    || getOrder?.status === ORDER_STATUS_CANCELED
                    || getOrder?.status === ORDER_STATUS_EXPIRED
                )
            );
        }

        if (getOrder) {
            const IconComponent = ORDER_TYPE_ICONS_BY_ID[getOrder.type as keyof typeof ORDER_TYPE_ICONS_BY_ID];
            setIconOrderType(() => IconComponent)

            const hideQRCode = company?.config?.checkout?.show_qrcode === false;

            switch (getOrder.status) {
                case ORDER_STATUS_PENDING:
                    setMessageByStatus(
                        getOrder.cart.table
                            ? t('order_placed.status_message.mounting_with_table', {tableName: getOrder.cart.table.name})
                            : hideQRCode
                                ? t('order_placed.status_message.pending_no_qrcode')
                                : t('order_placed.status_message.pending')
                    );
                    break;
                case ORDER_STATUS_MOUNTING:
                    setMessageByStatus(
                        getOrder.cart.table
                            ? t('order_placed.status_message.mounting_with_table', {tableName: getOrder.cart.table.name})
                            : hideQRCode
                                ? t('order_placed.status_message.mounting_no_qrcode')
                                : t('order_placed.status_message.mounting')
                    );
                    break;
                case ORDER_STATUS_RELEASED_TAKEAWAY:
                case ORDER_STATUS_RELEASED_SHIPMENT:
                    setMessageByStatus(
                        getOrder.cart.table
                            ? t('order_placed.status_message.shipment_with_table', {tableName: getOrder.cart.table.name})
                            : hideQRCode
                                ? t('order_placed.status_message.shipment_no_qrcode')
                                : t('order_placed.status_message.shipment')
                    );
                    break;
                case ORDER_STATUS_DONE:
                    setMessageByStatus(
                        getOrder.cart.table
                            ? t('order_placed.status_message.done_with_table', {tableName: getOrder.cart.table.name})
                            : hideQRCode
                                ? t('order_placed.status_message.done_no_qrcode')
                                : t('order_placed.status_message.done')
                    );
                    break;
                default:
                    break;
            }
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOrder])

    useEffect(() => {
        if (isReleased && !readNotifyReleased) {
            showReleasedMessage();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReleased, readNotifyReleased]);

    useEffect(() => {
        if (!isMobile || isInDrawer) {
            setShowOrderInfo(true);
        }
    }, [isMobile, isInDrawer]);

    useEffect(() => {
        const boxOrderInfo = document.getElementById('boxOrderInfo');
        if (boxOrderInfo) {
            goScrollTo(boxOrderInfo)
        }
    }, [showOrderInfo]);

    const showReleasedMessage = () => {
        vibrate(1500);
        setTimeout(() => {
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconSuccess(true);
            setMainLoadingTitle(`
                <div class='grid'>
                    <span>${t('order_placed.is_release.title')}</span>
                    <span class='text-xl font-extrabold'>${t('order_placed.is_release.subtitle')}</span>
                    <span class='text-4xl tracking-tighter font-black leading-none pt-2'>nº ${formatCartId(Number(localOrderId))}</span></div>
                    <div class='text-base font-medium leading-5 text-gray-500 py-3 px-4'>
                        ${ getOrder.cart.table ? t('order_placed.is_release.subtitle_2_with_table', {tableName: getOrder.cart.table.name}) : t('order_placed.is_release.subtitle_2')}
                    </div>
                </span>
            `);
            setMainLoadingMessage('');
            setMainLoadingLabelCloseButton(getOrder.cart.table ? t('order_placed.is_release.close_button_label_with_table') : t('order_placed.is_release.close_button_label'))
            setMainLoadingCloseButton(true);
            setReadNotifyReleased(true);
        }, 1000);
    }

    const handleExpirePix = (type: number) => {
        if (type === ORDER_TYPE_STORE_COUNTER_ID) {
            cancelPayment(cartDisplay?.id)
                .then((response: any) => {
                    if (response.success) {
                        setMainLoading(true);
                        setMainLoadingIcon(false);
                        setMainLoadingIconError(true);
                        setMainLoadingTitle(t('order_placed.pix.code_expired_title'));
                        setMainLoadingMessage(t('order_placed.pix.code_expired_message'));
                        setMainLoadingCloseButton(true);
                        setOrder(null);
                        navigate(`${getLinkToHome()}/checkout`);
                    }
            });
        } else {
            getOrdersToday();
        }
    }

    useEffect(() => {
        if (!company && !loadingCompany) {
            loadCompanyInfo()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    const Icon = iconOrderType;

    if (!orderType || !currentStore) return null;

    return (
        <DefaultLayout showHeader={showHeader} showFooter={showFooter} isInDrawer={isInDrawer}>

            <StampStatus status_name={getOrder?.status_name} show={!loadingGetOrder && showStampStatus} />

            <section className={`flex gap-6 flex-col items-center justify-between w-full h-full ${isInDrawer ? 'p-4' : ''}`}>
                {loadingGetOrder
                    ?
                        <div className="flex items-center justify-center gap-1 h-full text-gray-500">
                            <ImSpinner8 className="animate-spin" />
                            <span>
                                {localOrderId 
                                    ? `${t('cart.loading_order')} nº ${ formatCartId(Number(localOrderId)) }` 
                                    : t('layout.loading')
                                }
                            </span>
                        </div>
                    :   getOrder &&
                            <div className="flex flex-col h-full w-full pt-2">

                                {showBreadcrumbOrder &&
                                    <div className={`grid gap-4 pt-2 ${!isInDrawer ? 'md:py-4 md:pb-6' : ''} ${isReleased ? 'pb-4' : 'pb-6'}`}>
                                        <div className="grid gap-1 font-medium text-center leading-none">
                                            <span className="text-2xl font-bold leading-none text-shopfood-title">
                                                {t('order_placed.title_hello', {user: user?.name.trim()})}
                                            </span>
                                            <span className="text-base text-gray-500">{t('cart.track_your_order')} { getOrder && <span className='font-extrabold text-shopfood-title'>nº { formatCartId(Number(getOrder.id)) }</span>}</span>
                                        </div>
                                        <section className="flex w-full items-center justify-center px-2">
                                            <div className={`w-full mx-2 ${!isInDrawer ? 'md:mx-0 md:max-w-lg' : ''}`}><BreadcrumbOrder isInDrawer={isInDrawer} order={getOrder} /></div>
                                        </section>
                                    </div>
                                }

                                <section className="flex flex-col gap-4 h-full w-full">

                                    {!isReleased && (isMobile || isInDrawer) &&
                                        <h2 className="grid text-center md:pb-2">
                                            <span className="font-medium">{t('cart.order_status')}</span>
                                            <span className="text-3xl font-extrabold leading-none text-shopfood-title">{getOrder.status_name }</span>
                                        </h2>
                                    }

                                    <section className={`flex flex-col ${!isInDrawer ? `md:flex-row` : ''} gap-4 justify-start w-full h-full transition-all ease-in-out`}>

                                        {/* QRCODE ORDER */}
                                        {!showPixCode && showOrderQRCode &&
                                            <div className={`flex flex-col gap-4 justify-center items-center w-full h-full ${isInDrawer ? 'pt-8' : ''} rounded-lg md:shadow-lg md:shadow-gray-100 md:ring-1 ring-gray-100
                                                ${!loadingGetOrder && isReleased ? 'bg-shopfood-success rounded-xl pt-4 pb-4 text-white' : ' text-shopfood-title'}
                                            `}>

                                                {!loadingGetOrder && isReleased &&
                                                    <h2 className="grid text-center py-2 md:pb-4">
                                                        <span className="text-lg font-semibold leading-none">{t('order_placed.is_release.title_2')}</span>
                                                        <span className="text-3xl font-extrabold leading-none text-neutral-800">{getOrder.status_name }</span>
                                                    </h2>
                                                }

                                                <BoxContentQRCodeOrder
                                                    code={getOrder.id}
                                                    show={showOrderQRCode}
                                                    validated={isValidated}
                                                    order_status={Number(getOrder.status)}
                                                    className={`${isReleased && 'ring-black/100 bg-white'}`}
                                                    hideQRCode={company?.config?.checkout?.show_qrcode === false}
                                                />
                                                {messageByStatus &&
                                                    <div className="grid px-4 pb-4 text-center">
                                                        <span className={`font-extrabold leading-none ${isInDrawer ? 'text-xl' : 'text-2xl'}`} dangerouslySetInnerHTML={{ __html: sanitizeText(messageByStatus) }} />
                                                    </div>
                                                }
                                                {getOrder.points > 0 && (
                                                    <CardPointsEarned points={getOrder.points} className="mx-auto mb-8" />
                                                )}
                                            </div>
                                        }

                                        {/* QRCODE PIX */}
                                        { showPixCode && <div className="flex  justify-center md:pt-4 items-center w-full h-full max-h-max md:max-h-full rounded-lg md:shadow-lg md:shadow-gray-100 md:ring-1 ring-gray-100"><QRCodePix order={getOrder} onClose={() => setShowPixCode(false)} onExpire={() => handleExpirePix(getOrder.type)} /></div>}

                                        <section className={`flex gap-4 w-full ${showPixCode || showOrderQRCode ? 'flex-col h-full' : `flex-col ${!isInDrawer ? 'md:flex-row' : ''}`}`}>

                                            {/* BUTTON SHOW CART */}
                                            {
                                                isMobile
                                                && getOrder
                                                && getOrder.type !== ORDER_TYPE_DELIVERY_ID
                                                && getOrder.status !== ORDER_STATUS_CANCELED
                                                && getOrder.status !== ORDER_STATUS_EXPIRED
                                                && !isInDrawer
                                                &&
                                                <div className="flex justify-center">
                                                    <Button onClick={() => setShowOrderInfo(!showOrderInfo)} className={`text-sm gap-1 z-10`}>
                                                        <TbChevronDown className={`transition-transform duration-300 ${showOrderInfo ? "rotate-180" : ''}`} />
                                                        {showOrderInfo ? t('order_placed.hide_order_info') : t('order_placed.show_order_info')}
                                                    </Button>
                                                </div>
                                            }

                                            {/* ORDER INFO */}
                                            {showOrderInfo && getOrder.type !== ORDER_TYPE_STORE_COUNTER_ID &&
                                                <section className={`flex flex-col gap-4 w-full px-4 rounded-xl bg-gray-50 ring-1 ring-gray-100
                                                transition-all ease-in-out duration-300 ${showOrderInfo ? "opacity-100 py-4" : 'opacity-0 h-0'}
                                                `}>
                                                    <div className="flex gap-6 items-center justify-between w-full">
                                                        <div className="hidden md:flex flex-col text-sm w-full">
                                                            <span>{t('cart.order_status')}</span>
                                                            <span className={`${isInDrawer ? 'text-base' : 'text-2xl'} font-extrabold leading-none text-shopfood-title`}>{ getOrder.status_name }</span>
                                                        </div>
                                                        <div className="w-full">
                                                            <h4 className="text-sm flex items-center justify-between gap-2">
                                                                <span>{t('order_placed.order_type_lable')}</span>
                                                            </h4>
                                                            <div className={`flex items-center gap-1 font-bold text-gray-700 ${isInDrawer ? 'text-base' : 'text-xl'}`}>
                                                                <span>{Icon && <Icon size={isInDrawer ? 20 : 24} />}</span>
                                                                <span className="text-nowrap">{ getOrderTypeLabelById(getOrder?.type) }</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <hr className="border-dashed border-gray-300 hidden md:block" />

                                                    {getOrder &&
                                                        <>
                                                            {getOrder.cart.table &&
                                                                <div className="flex items-center gap-2 w-full text-sm">
                                                                    <span className="self-start"><MdOutlineTableRestaurant size={20} /></span>
                                                                    <div className="flex flex-col leading-none">
                                                                        <h4 className="leading-none font-semibold">{t('layout.table')}</h4>
                                                                        <p className="leading-none text-gray-500 mt-1">{ getOrder.cart.table.name }</p>
                                                                    </div>
                                                                </div>
                                                            }

                                                            <div className="flex items-center gap-2 w-full text-sm">
                                                                <span className="self-start"><TbClock size={20} /></span>
                                                                <div className="flex flex-col leading-none w-full">
                                                                    <h4 className="leading-none font-semibold">{t('order_placed.order_placed_on')}</h4>
                                                                    <p className="leading-none text-gray-500 mt-1">{ getOrder.created_at_formatted }</p>
                                                                </div>
                                                                <div className="self-start text-nowrap md:w-full">
                                                                    <Badge outline className="text-base font-bold leading-6 bg-white"> nº { formatCartId(Number(getOrder.id)) }</Badge>
                                                                </div>
                                                            </div>

                                                            {getOrder.observation &&
                                                                <div className="flex items-center gap-2 w-full text-sm">
                                                                    <span className="self-start"><TbMessage2Question size={20} /></span>
                                                                    <div className="flex flex-col leading-none">
                                                                        <h4 className="leading-none font-semibold">{t('layout.observation.observation')}</h4>
                                                                        <p className="leading-none text-gray-500 mt-1">{ getOrder.observation }</p>
                                                                    </div>
                                                                </div>
                                                            }

                                                            <div className="flex items-center gap-2 w-full text-sm">
                                                                <span className="self-start"><TbCashRegister size={20} /></span>
                                                                <div className="flex flex-col leading-none">
                                                                    <h4 className="leading-none font-semibold">{t('cart.value_details.payment_methods')}</h4>
                                                                    {getOrder.payment_methods?.map((method: any, index: number) =>
                                                                        <div key={index} className='text-gray-500 mt-1'>
                                                                            {method?.name}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </>
                                                    }

                                                    <div className="flex items-center gap-2 w-full text-sm">
                                                        <span className="self-start"><TbBuildingSkyscraper size={20} /></span>
                                                        <div className="flex flex-col leading-none">
                                                            <p className="leading-none text-gray-500 mt-1">
                                                                <span className="font-semibold">{ currentStore.name }</span> - { currentStore.neighborhood }
                                                            </p>
                                                        </div>
                                                    </div>

                                                </section>
                                            }

                                            {/* CART */}
                                            {showOrderInfo &&
                                                <section id="boxOrderInfo" className={`shadow-lg shadow-gray-100 rounded-xl w-full md:h-full border border-dashed border-gray-300
                                                    transition-all ease-in-out duration-300 ${showOrderInfo ? "opacity-100 py-4" : 'opacity-0 h-0'}`}
                                                    >
                                                    {localCart && <Cart getCart={localCart} order={getOrder} onCloseCart={() => {}} onlyInfo />}
                                                </section>
                                            }

                                        </section>


                                    </section>
                                </section>
                            </div>
                }
            </section>
        </DefaultLayout>
    );
};

export default OrderPlaced;